import { useMemo, useState } from 'react';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { fmtDate, fmtMoney } from '../lib/format';
import { Badge, Button, Card, Empty, PageHeader } from '../ui/kit';
import { RecordDrawer, type FieldSpec } from '../ui/RecordDrawer';

type Row = Record<string, unknown> & {
  id: string;
  title: string;
  contract_no: string | null;
  project_no: string | null;
  building_id: string | null;
  contractor_org_id: string | null;
  architect_org_id: string | null;
  sum_vep: number | null;
  deposit_pct: number | null;
  retention_pct: number | null;
  signed_on: string | null;
  status: string;
  deleted_at: string | null;
};

const NONE = '00000000-0000-0000-0000-000000000000';
const STATUS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'practical_completion', label: 'Practical completion' },
  { value: 'closed', label: 'Closed' },
];

export default function Contracts() {
  const { siteId, site, can } = useApp();
  const [open, setOpen] = useState<Row | null | 'new'>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const list = useRows<Row>(() => supabase.from('contracts').select('*').eq('site_id', siteId ?? NONE).order('created_at'), [siteId], ['contracts']);
  const buildings = useRows<{ id: string; name: string }>(
    () => supabase.from('buildings').select('id, name').eq('site_id', siteId ?? NONE).is('deleted_at', null).order('sort'),
    [siteId],
    ['buildings'],
  );
  const orgs = useRows<{ id: string; name: string; kind: string }>(
    () => supabase.from('organisations').select('id, name, kind').is('deleted_at', null).order('name'),
    [],
    ['organisations'],
  );

  const name = (rows: { id: string; name: string }[], id: string | null) => rows.find((r) => r.id === id)?.name ?? '—';

  const fields = useMemo<FieldSpec[]>(
    () => [
      { key: 'title', label: 'Title', required: true },
      { key: 'building_id', label: 'Building', type: 'select', options: buildings.rows.map((b) => ({ value: b.id, label: b.name })) },
      {
        key: 'contractor_org_id',
        label: 'Contractor',
        type: 'select',
        options: orgs.rows.filter((o) => ['contractor', 'supplier', 'other'].includes(o.kind)).map((o) => ({ value: o.id, label: o.name })),
      },
      {
        key: 'architect_org_id',
        label: 'Architect of record',
        type: 'select',
        hint: 'Signs the payment certificates',
        options: orgs.rows.filter((o) => o.kind === 'architect').map((o) => ({ value: o.id, label: o.name })),
      },
      { key: 'contract_no', label: 'Contract / client ref no.', hint: 'e.g. 77015658' },
      { key: 'project_no', label: 'Contractor project no.', hint: 'e.g. 1425' },
      { key: 'sum_vep', label: 'Contract sum (FJD, VAT excl.)', type: 'number' },
      { key: 'deposit_pct', label: 'Deposit %', type: 'number' },
      { key: 'retention_pct', label: 'Retention %', type: 'number' },
      { key: 'signed_on', label: 'Signed on', type: 'date' },
      { key: 'status', label: 'Status', type: 'select', options: STATUS, required: true },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    [buildings.rows, orgs.rows],
  );

  const editable = can('contracts', 4);
  const shown = list.rows.filter((r) => showDeleted || !r.deleted_at);

  return (
    <>
      <PageHeader title="Contracts" sub={site?.name} actions={editable ? <Button kind="primary" onClick={() => setOpen('new')}>+ Add contract</Button> : null} />
      <div className="toolbar">
        <label className="check small">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} /> Show deleted
        </label>
      </div>
      <Card pad={false}>
        {list.error ? <div className="error-box">{list.error}</div> : null}
        {list.loading ? (
          <Empty>Loading…</Empty>
        ) : shown.length === 0 ? (
          <Empty>No contracts yet.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Contract</th>
                  <th>Building</th>
                  <th>Contractor</th>
                  <th>Ref</th>
                  <th>Sum (VEP)</th>
                  <th>Deposit / retention</th>
                  <th>Architect of record</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} className={`click ${r.deleted_at ? 'deleted' : ''}`} onClick={() => setOpen(r)}>
                    <td>
                      <strong>{r.title}</strong>
                      {r.signed_on ? <div className="muted small">signed {fmtDate(r.signed_on)}</div> : null}
                    </td>
                    <td>{name(buildings.rows, r.building_id)}</td>
                    <td>{name(orgs.rows, r.contractor_org_id)}</td>
                    <td>
                      {r.contract_no ?? '—'}
                      {r.project_no ? <div className="muted small">proj {r.project_no}</div> : null}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtMoney(r.sum_vep)}</td>
                    <td>
                      {r.deposit_pct ?? '—'}% / {r.retention_pct ?? '—'}%
                    </td>
                    <td>{name(orgs.rows, r.architect_org_id)}</td>
                    <td>
                      <Badge>{STATUS.find((s) => s.value === r.status)?.label ?? r.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <RecordDrawer
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open === 'new' ? 'Add contract' : open ? open.title : ''}
        table="contracts"
        fields={fields}
        record={open === 'new' ? null : open}
        defaults={{ site_id: siteId, status: 'active' }}
        canEdit={editable}
        onSaved={() => void list.reload()}
        label={(r) => `contract ${String(r.title ?? '')}`}
      />
    </>
  );
}
